/**
 * NOVAPAY-116-R2 — keep Mini App open after order / payment / referral.
 * No scheduled WebApp.close. Close only via explicit customer action or Telegram chrome X.
 */

const SUCCESS_STORAGE_KEY = "asiastyle.store-v2.post-order.v1";

/**
 * STAGING-LAUNCH-READINESS-002-R2-R2 — deliberately duplicated (not imported) from
 * `@asiastyle/surface-ui`'s `isTelegramMiniAppContext`: this module is loaded directly
 * by node-based guard tests (no bundler), and pulling in the surface-ui package here
 * drags in .tsx-only submodules that the guard test's lightweight ESM loader cannot
 * resolve. Keep this in sync with packages/surface-ui/src/global-app-close/telegram-close.ts.
 */
function isTelegramMiniAppContext(): boolean {
  const wa = (window as unknown as { Telegram?: { WebApp?: { initData?: string; platform?: string } } })
    .Telegram?.WebApp;
  if (!wa) return false;
  const initData = String(wa.initData ?? "").trim();
  if (initData.length > 0) return true;
  const platform = String(wa.platform ?? "").trim();
  return platform.length > 0 && platform !== "unknown";
}

export type PersistedSuccessView = {
  orderId: string;
  displayNumber?: string;
  status?: string;
  paymentMethod?: string;
  totalUah?: number;
  contactName?: string;
  contactPhone?: string;
  savedAt: number;
};

type TelegramWebAppClose = {
  close?: () => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
};

let closePatched = false;
let allowExplicitClose = false;

export function persistSuccessView(view: Omit<PersistedSuccessView, "savedAt">): void {
  try {
    const payload: PersistedSuccessView = { ...view, savedAt: Date.now() };
    window.sessionStorage.setItem(SUCCESS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* storage unavailable */
  }
}

export function readPersistedSuccessView(maxAgeMs = 2 * 60 * 60 * 1000): PersistedSuccessView | null {
  try {
    const raw = window.sessionStorage.getItem(SUCCESS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSuccessView;
    if (!parsed?.orderId || typeof parsed.savedAt !== "number") return null;
    if (Date.now() - parsed.savedAt > maxAgeMs) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPersistedSuccessView(): void {
  try {
    window.sessionStorage.removeItem(SUCCESS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Block accidental / host-triggered WebApp.close while on post-order surface.
 *
 * STAGING-LAUNCH-READINESS-002-R2-R2 — once an order exists (paid OR payment-pending)
 * there is nothing left for the customer to "lose": never arm Telegram's native
 * closing-confirmation warning here, even if checkout left it enabled. We still guard
 * against accidental/host-triggered `WebApp.close()` calls by patching `close` itself,
 * which is an orthogonal concern from the native "unsaved changes" prompt.
 */
export function armPostOrderCloseGuard(): void {
  const webApp = (window as unknown as { Telegram?: { WebApp?: TelegramWebAppClose } }).Telegram?.WebApp;
  if (!webApp) return;
  try {
    webApp.disableClosingConfirmation?.();
  } catch {
    /* older clients */
  }
  if (closePatched || typeof webApp.close !== "function") return;
  const original = webApp.close.bind(webApp);
  webApp.close = () => {
    if (allowExplicitClose) {
      allowExplicitClose = false;
      original();
      return;
    }
    // Swallow automatic / non-explicit close attempts (e.g. host side-effects) without
    // re-arming the native "unsaved changes" warning — the order is already saved.
    try {
      webApp.disableClosingConfirmation?.();
    } catch {
      /* ignore */
    }
  };
  closePatched = true;
}

export function disarmPostOrderCloseGuard(): void {
  const webApp = (window as unknown as { Telegram?: { WebApp?: TelegramWebAppClose } }).Telegram?.WebApp;
  try {
    webApp?.disableClosingConfirmation?.();
  } catch {
    /* ignore */
  }
}

/** Explicit close arm — pairs with GlobalAppClose / closeStoreExplicitly. */
export function armExplicitStoreClose(): void {
  allowExplicitClose = true;
}

export type CloseStoreResult = "closed" | "browser_fallback";

/**
 * Explicit in-app «Close store» — only path that may call WebApp.close.
 *
 * STAGING-LAUNCH-READINESS-002-R2-R2 — use the same Telegram-context detection as the
 * header «×» button (`isTelegramMiniAppContext`, initData OR known platform) instead of
 * a stricter initData-only check, so this path and GlobalAppCloseButton never disagree
 * about whether a real Telegram close is available.
 */
export function closeStoreExplicitly(): CloseStoreResult {
  const webApp = (window as unknown as { Telegram?: { WebApp?: TelegramWebAppClose } }).Telegram?.WebApp;
  if (typeof webApp?.close === "function" && isTelegramMiniAppContext()) {
    allowExplicitClose = true;
    webApp.close();
    return "closed";
  }
  allowExplicitClose = false;
  return "browser_fallback";
}

/** Test helper: ensure source does not schedule auto-close. */
export function hasScheduledWebAppClose(source: string): boolean {
  return /setTimeout\s*\([^)]*close\s*\(/i.test(source) || /WebApp\.close\s*\(\s*\)/.test(source);
}
